import { useContext, useEffect, useState } from "react";
import { Box, Button, Typography, Alert } from "@mui/material";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { AuthContext } from "../context/AuthContext";
import { DeviceContext } from "../context/DeviceContext";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const sections = [
  {
    num: "1",
    title: "Service Scope",
    body: "StonksCentral is a community discussion platform for retail investing topics. We are not a broker-dealer, investment adviser, or fiduciary. Platform content is user-generated and informational.",
  },
  {
    num: "2",
    title: "Financial Disclaimer",
    body: "Nothing on this platform is financial, tax, legal, accounting, or investment advice. You are solely responsible for your investment decisions. Investing in stocks, options, crypto, and other assets includes risk of loss, including total loss.",
  },
  {
    num: "3",
    title: "Accuracy and Market Data",
    body: "We do not guarantee accuracy, completeness, timeliness, or suitability of any post, comment, ranking, quote, chart, or market data. Third-party data and links are provided as-is and may be delayed or incorrect.",
  },
  {
    num: "4",
    title: "User Content and Moderation",
    body: "You are responsible for the content you post. You must not post fraud, market manipulation, impersonation, unlawful promotions, doxxing, harassment, spam, or intellectual property violations. We may remove content, limit visibility, suspend, or terminate accounts at our discretion to protect users and platform integrity.",
  },
  {
    num: "5",
    title: "Bot Policy",
    body: "Bot accounts are allowed only when explicitly registered as bot accounts. Misrepresenting bot accounts as human accounts is prohibited and may result in account closure.",
  },
  {
    num: "6",
    title: "Securities and Promotions",
    body: "You must clearly disclose material conflicts, paid promotions, and compensation related to securities discussions. Undisclosed touting or deceptive endorsements are prohibited.",
  },
  {
    num: "7",
    title: "Copyright / DMCA",
    body: "Do not upload content you do not own or have rights to use. We may process copyright complaints and remove infringing content. Repeat infringers may be terminated.",
  },
  {
    num: "8",
    title: "Privacy and Data Use",
    body: "By using the service you consent to collection and processing of data required to operate, secure, and improve the platform, subject to our Privacy Policy. Users may have additional rights under applicable privacy laws.",
  },
  {
    num: "9",
    title: "Age Restrictions",
    body: "The service is not intended for children under 13. If you are under the age of majority in your jurisdiction, you may use the service only with required parental/guardian consent.",
  },
  {
    num: "10",
    title: "Warranties and Liability",
    body: 'The service is provided "as is" and "as available," without warranties of any kind. To the maximum extent permitted by law, StonksCentral is not liable for indirect, incidental, consequential, punitive, or lost profit damages arising from platform use, third-party content, or market outcomes.',
  },
  {
    num: "11",
    title: "Changes to Terms",
    body: "We may update these terms periodically. Continued use after updates may require re-acceptance. The version shown above is the active version.",
  },
];

function TermsPage() {
  const { isMobile } = useContext(DeviceContext);
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentVersion, setCurrentVersion] = useState("2026-02-21");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const required = searchParams.get("required") === "true";
  const isNewTab = window.history.length <= 1;

  const handleBack = () => {
    if (isNewTab) window.close();
    else navigate(-1);
  };

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/legal/terms/meta`
        );
        setCurrentVersion(response.data.currentTermsVersion || "2026-02-21");
      } catch {
        setCurrentVersion(user?.currentTermsVersion || "2026-02-21");
      }
    };
    fetchMeta();
  }, [user]);

  const acceptTerms = async () => {
    if (!user?.token) { navigate("/signin"); return; }
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/legal/terms/accept`,
        { version: currentVersion },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setSuccess("Terms accepted. Thank you.");
      const updatedUser = {
        ...user,
        termsAcceptedVersion: response.data.currentTermsVersion,
        currentTermsVersion: response.data.currentTermsVersion,
        requiresTermsAcceptance: false,
      };
      setUser(updatedUser);
      const cookieUser = { ...updatedUser };
      delete cookieUser.token;
      Cookies.set("user", JSON.stringify(cookieUser), { expires: 7, secure: true, sameSite: "Lax" });
      setTimeout(() => navigate("/"), 700);
    } catch (e) {
      setError(e.response?.data?.message || "Could not record acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.05) 0%, ${DL.bgDark} 55%)`,
        px: 2,
        py: isMobile ? 3 : 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Back link */}
      <Box
        onClick={handleBack}
        sx={{
          alignSelf: "flex-start",
          width: "100%",
          maxWidth: 760,
          mb: 2,
          cursor: "pointer",
          color: DL.textMuted,
          "&:hover": { color: DL.green },
          transition: "color 0.2s ease",
        }}
      >
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>← Back to home</Typography>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 760,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: isMobile ? 3 : 4,
            py: 3,
            borderBottom: `1px solid ${DL.divider}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: isMobile ? "1.4rem" : "1.75rem", color: DL.textPrimary, mb: 0.5 }}>
            Terms and Conditions
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted }}>
            Version {currentVersion} · Effective date: February 21, 2026
          </Typography>
        </Box>

        {/* Alerts */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {required && (
            <Alert
              severity="warning"
              sx={{ borderRadius: "8px", fontSize: "0.875rem" }}
            >
              You must accept the current Terms and Conditions before continuing.
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ borderRadius: "8px", backgroundColor: "rgba(255,100,103,0.1)", color: DL.red, border: "1px solid rgba(255,100,103,0.25)", "& .MuiAlert-icon": { color: DL.red } }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ borderRadius: "8px", backgroundColor: "rgba(98,244,129,0.1)", color: DL.green, "& .MuiAlert-icon": { color: DL.green } }}
            >
              {success}
            </Alert>
          )}
        </Box>

        {/* Sections */}
        <Box sx={{ px: isMobile ? 3 : 4, pt: 3, pb: 1, display: "flex", flexDirection: "column", gap: 0 }}>
          {sections.map((s, i) => (
            <Box
              key={s.num}
              sx={{
                py: 2.5,
                borderBottom: i < sections.length - 1 ? `1px solid ${DL.border}` : "none",
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              {/* Number badge */}
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(98,244,129,0.15)",
                  border: `1px solid rgba(98,244,129,0.25)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: DL.green,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  mt: 0.25,
                  flexShrink: 0,
                }}
              >
                {s.num}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: DL.textPrimary, mb: 0.75 }}>
                  {s.title}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: DL.textSecondary, lineHeight: 1.7 }}>
                  {s.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Disclaimer */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 2, borderTop: `1px solid ${DL.border}`, backgroundColor: DL.bgDark }}>
          <Typography sx={{ fontSize: "0.75rem", color: DL.textMuted, fontStyle: "italic" }}>
            This page is a platform terms template and not legal advice. Final production terms should be reviewed by licensed counsel.
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            onClick={acceptTerms}
            disabled={submitting}
            sx={{
              background: `linear-gradient(to right, #10b981, #059669)`,
              color: "#fff",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              fontWeight: 700,
              fontSize: "0.9rem",
              textTransform: "none",
              boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
              "&:hover": { filter: "brightness(110%)" },
              "&:disabled": { opacity: 0.5 },
              transition: "all 0.2s ease",
            }}
          >
            {submitting ? "Saving…" : "I Accept"}
          </Button>
          <Button
            onClick={handleBack}
            sx={{
              backgroundColor: "transparent",
              border: `1px solid ${DL.borderSubtle}`,
              color: DL.textSecondary,
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              fontWeight: 600,
              fontSize: "0.9rem",
              textTransform: "none",
              "&:hover": { borderColor: DL.green, color: DL.textPrimary },
              transition: "all 0.2s ease",
            }}
          >
            {isNewTab ? "Close" : "Back"}
          </Button>
        </Box>
      </Box>

      <Typography sx={{ mt: 3, fontSize: "0.75rem", color: DL.textMuted }}>
        © {new Date().getFullYear()} StonksCentral. All rights reserved.
      </Typography>
    </Box>
  );
}

export default TermsPage;
